<p class="font-bold line-height-2" i18n>
  File System Profile Elements define access conditions that allow specific operations on ADF/DF/EF's - e.g., read, update, resize, delete.
  PIN is an umbrella term to indicate the user verification requirement and it's comprised of key references that can be assigned to the
  operations above. Commonly used PINs are Global PIN1 and ADM1.
</p>

<p class="font-bold line-height-2" i18n>The follow table contains the recommended settings. Please update as needed.</p>

<div class="mx-6">
  <rs-error-message *ngIf="errorFound" class="mb-3" [message]="errorMessage" />
  <p-table
    showGridlines="true"
    stripedRows="true"
    [value]="profileValues.pins"
    [(selection)]="selectedPins"
    dataKey="id"
    sortField="type"
    [sortOrder]="1"
    styleClass="p-datatable-striped p-datatable-gridlines"
  >
    <ng-template pTemplate="caption">
      <div class="flex align-items-center gap-2">
        <p-button i18n-label label="Add PIN" icon="pi pi-plus-circle" size="small" [outlined]="true" (onClick)="addPin()"></p-button>
        <!-- Currently disabling the ability to add local PINs from this wizard section -->
        <!-- <p-button
          i18n-label
          label="Add Local PIN"
          icon="pi pi-plus-circle"
          size="small"
          [outlined]="true"
          (onClick)="addLocalPin()"
        ></p-button> -->
        <p-button i18n-label label="Add ADM" icon="pi pi-plus-circle" size="small" [outlined]="true" (onClick)="addAdm()"></p-button>
        <p-button
          i18n-label
          label="Delete"
          icon="pi pi-trash"
          size="small"
          severity="danger"
          [outlined]="true"
          (onClick)="deletePins()"
          [disabled]="!selectedPins || !selectedPins.length"
        ></p-button>
      </div>
    </ng-template>
    <ng-template pTemplate="colgroup">
      <colgroup>
        <col style="width: 3rem" />
        <col style="width: 8rem" />
        <col style="width: 12rem" />
        <col />
        <col />
        <col style="width: 10rem" />
        <col style="width: 10rem" />
      </colgroup>
    </ng-template>
    <ng-template pTemplate="header">
      <tr>
        <th>
          <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
        </th>
        <th i18n>PIN #</th>
        <th>
          <div i18n>PIN Size</div>
          <div class="font-medium" i18n>(4 to 8 digits)</div>
        </th>
        <th i18n>Value</th>
        <th i18n>Pin Attributes</th>
        <th>
          <div i18n>Max Attempts</div>
          <div class="font-medium" i18n>(0 to 15)</div>
        </th>
        <th>
          <div i18n>Retries Left</div>
          <div class="font-medium" i18n>(0 to 15)</div>
        </th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-pin>
      <tr>
        <td style="text-align: center">
          <p-tableCheckbox [value]="pin"></p-tableCheckbox>
        </td>
        <td style="text-align: center">
          <p-inputMask
            styleClass="maximize-inputs console-text"
            [(ngModel)]="pin.number"
            [mask]="+pin.type === 1 ? 'PIN9' : +pin.type === 2 ? 'LOCALPIN9' : 'ADM9?9'"
            [unmask]="true"
            slotChar=""
            [autoClear]="false"
            (onBlur)="handleFormEvents()"
          ></p-inputMask>
        </td>
        <td style="text-align: center">
          <ng-container *ngIf="+pin.type === 1 || +pin.type === 2">
            <p-inputNumber
              class="maximize-inputs"
              inputStyleClass="maximize-inputs centered-inputs"
              [(ngModel)]="pin.size"
              mode="decimal"
              [showButtons]="false"
              (onBlur)="handleFormEvents()"
            ></p-inputNumber>
          </ng-container>
          <ng-container *ngIf="+pin.type === 3">
            <div>
              <div>
                <p-radioButton
                  [(ngModel)]="pin.size"
                  name="pin.admPinType"
                  [inputId]="'pin[' + pin.id + '].admPinType#1'"
                  [value]="8"
                  (onClick)="parseAdmPinValue(pin); handleFormEvents()"
                ></p-radioButton>
                <label class="ml-2 label-clickable nobr" [for]="'pin[' + pin.id + '].admPinType#1'" i18n>8 Digits</label>
              </div>
              <div>
                <p-radioButton
                  [(ngModel)]="pin.size"
                  name="pin.admPinType"
                  [inputId]="'pin[' + pin.id + '].admPinType#2'"
                  [value]="16"
                  (onClick)="handleFormEvents()"
                ></p-radioButton>
                <label class="ml-2 label-clickable nobr" [for]="'pin[' + pin.id + '].admPinType#2'" i18n>16 HEX Chars</label>
              </div>
            </div>
          </ng-container>
        </td>
        <td style="text-align: center">
          <table width="100%" cellspacing="0" cellpadding="0">
            <tr>
              <td>
                <table style="text-align: left" class="mb-3">
                  <tr>
                    <td>
                      <p-radioButton
                        (onClick)="handleFormEvents()"
                        name="pin.valueGeneration"
                        [inputId]="'pin[' + pin.id + '].valueGeneration#1'"
                        value="1"
                        [(ngModel)]="pin.valueGeneration"
                      ></p-radioButton>
                    </td>
                    <td>
                      <label class="ml-2 label-clickable nobr" [for]="'pin[' + pin.id + '].valueGeneration#1'" i18n>eDGAR Generated</label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <p-radioButton
                        (onClick)="handleFormEvents()"
                        name="pin.valueGeneration"
                        [inputId]="'pin[' + pin.id + '].valueGeneration#2'"
                        value="2"
                        [(ngModel)]="pin.valueGeneration"
                      ></p-radioButton>
                    </td>
                    <td>
                      <label class="ml-2 label-clickable nobr" [for]="'pin[' + pin.id + '].valueGeneration#2'" i18n>User Defined</label>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr *ngIf="+pin.valueGeneration === 2">
              <ng-container *ngIf="+pin.type === 1 || +pin.type === 2 || pin.size === 8">
                <td style="width: 7rem">
                  <input
                    pInputText
                    rsNumbersOnlyInput
                    class="maximize-inputs console-text"
                    [maxlength]="pin.size"
                    [(ngModel)]="pin.value"
                    (input)="handleFormEvents()"
                    (focus)="showTip(C.TIP_EWIZ_KEY_DATA_VALUE)"
                    (blur)="clearDynamicToast()"
                  />
                </td>
              </ng-container>
              <ng-container *ngIf="+pin.type === 3 && pin.size !== 8">
                <td style="width: 7rem">
                  <input
                    pInputText
                    rsHexOnlyInput
                    class="maximize-inputs console-text"
                    [maxlength]="pin.size"
                    [(ngModel)]="pin.value"
                    (input)="handleFormEvents()"
                    (focus)="showTip(C.TIP_EWIZ_KEY_DATA_VALUE)"
                    (blur)="clearDynamicToast()"
                  />
                </td>
              </ng-container>
            </tr>
          </table>
        </td>
        <td *ngIf="+pin.type === 1 || +pin.type === 2">
          <table cellspacing="0" cellpadding="0" style="margin: auto !important; width: auto !important">
            <tr>
              <td colspan="4" style="text-align: center">
                <div class="mb-2"><strong i18n>PIN State Change</strong></div>
              </td>
            </tr>
            <tr>
              <td style="width: 2rem">
                <p-radioButton
                  (onClick)="handleFormEvents()"
                  name="pin.attributes.stateChange"
                  [inputId]="'pin[' + pin.id + '].attributes.stateChange#1'"
                  value="1"
                  [(ngModel)]="pin.attributes.stateChange"
                ></p-radioButton>
              </td>
              <td>
                <label
                  class="ml-2 mr-3 label-clickable"
                  style="white-space: nowrap !important"
                  [for]="'pin[' + pin.id + '].attributes.stateChange#1'"
                  i18n
                >
                  allowed
                </label>
              </td>
              <td style="width: 2rem">
                <p-radioButton
                  (onClick)="handleFormEvents()"
                  name="pin.attributes.stateChange"
                  [inputId]="'pin[' + pin.id + '].attributes.stateChange#2'"
                  value="2"
                  [(ngModel)]="pin.attributes.stateChange"
                ></p-radioButton>
              </td>
              <td>
                <label
                  class="ml-2 mr-3 label-clickable"
                  style="white-space: nowrap !important"
                  [for]="'pin[' + pin.id + '].attributes.stateChange#2'"
                  i18n
                >
                  not allowed
                </label>
              </td>
            </tr>
            <tr>
              <td colspan="4" style="text-align: center">
                <div class="mt-3 mb-2"><strong i18n>PIN Can Be Changed</strong></div>
              </td>
            </tr>
            <tr>
              <td>
                <p-radioButton
                  (onClick)="handleFormEvents()"
                  name="pin.attributes.pinChange"
                  [inputId]="'pin[' + pin.id + '].attributes.pinChange#1'"
                  value="1"
                  [(ngModel)]="pin.attributes.pinChange"
                ></p-radioButton>
              </td>
              <td>
                <label
                  class="ml-2 mr-3 label-clickable"
                  style="white-space: nowrap !important"
                  [for]="'pin[' + pin.id + '].attributes.pinChange#1'"
                  i18n
                >
                  allowed
                </label>
              </td>
              <td>
                <p-radioButton
                  (onClick)="handleFormEvents()"
                  name="pin.attributes.pinChange"
                  [inputId]="'pin[' + pin.id + '].attributes.pinChange#2'"
                  value="2"
                  [(ngModel)]="pin.attributes.pinChange"
                ></p-radioButton>
              </td>
              <td>
                <label
                  class="ml-2 mr-3 label-clickable"
                  style="white-space: nowrap !important"
                  [for]="'pin[' + pin.id + '].attributes.pinChange#2'"
                  i18n
                >
                  not allowed
                </label>
              </td>
            </tr>
            <tr>
              <td colspan="4" style="text-align: center">
                <div class="mt-3 mb-2"><strong i18n>PIN Is</strong></div>
              </td>
            </tr>
            <tr>
              <td>
                <p-radioButton
                  (onClick)="handleFormEvents()"
                  name="pin.attributes.pinEnabled"
                  [inputId]="'pin[' + pin.id + '].attributes.pinEnabled#1'"
                  value="1"
                  [(ngModel)]="pin.attributes.pinEnabled"
                ></p-radioButton>
              </td>
              <td>
                <label
                  class="ml-2 mr-3 label-clickable"
                  style="white-space: nowrap !important"
                  [for]="'pin[' + pin.id + '].attributes.pinEnabled#1'"
                  i18n
                >
                  enabled
                </label>
              </td>
              <td>
                <p-radioButton
                  (onClick)="handleFormEvents()"
                  name="pin.attributes.pinEnabled"
                  [inputId]="'pin[' + pin.id + '].attributes.pinEnabled#2'"
                  value="2"
                  [(ngModel)]="pin.attributes.pinEnabled"
                ></p-radioButton>
              </td>
              <td>
                <label
                  class="ml-2 mr-3 label-clickable"
                  style="white-space: nowrap !important"
                  [for]="'pin[' + pin.id + '].attributes.pinEnabled#2'"
                  i18n
                >
                  disabled
                </label>
              </td>
            </tr>
          </table>
        </td>
        <td *ngIf="+pin.type === 3" style="text-align: center" i18n>N/A</td>
        <td *ngIf="+pin.type === 1 || +pin.type === 2" style="text-align: center">
          <p-inputNumber
            class="maximize-inputs"
            inputStyleClass="maximize-inputs centered-inputs"
            [(ngModel)]="pin.maxAttempts"
            mode="decimal"
            [showButtons]="false"
            (onBlur)="handleFormEvents()"
          ></p-inputNumber>
        </td>
        <td *ngIf="+pin.type === 3" style="text-align: center" i18n>N/A</td>
        <td *ngIf="+pin.type === 1 || +pin.type === 2" style="text-align: center">
          <p-inputNumber
            class="maximize-inputs"
            inputStyleClass="maximize-inputs centered-inputs"
            [(ngModel)]="pin.retryNumberLeft"
            mode="decimal"
            [showButtons]="false"
            (onBlur)="handleFormEvents()"
          ></p-inputNumber>
        </td>
        <td *ngIf="+pin.type === 3" style="text-align: center" i18n>N/A</td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="7" i18n>No PINs defined.</td>
      </tr>
    </ng-template>
  </p-table>
</div>
